import { AccessoryTypeType } from '@alpinehomeair/common';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import {
  useGetQuotableSystemsAccessoriesRecommendationsQuery,
  useGetQuotableSystemsQuery
} from 'features/api/project-api';
import {
  updateSelectedDuctedSystem,
  updateSelectedSystem
} from 'features/systems';
import { useEffect, useMemo, useState } from 'react';
import { COMPATIBILITY_MATCH_TAG } from 'utils/compatibility-systems';
import {
  MANUAL_ACCESSORY_TYPE,
  toManualAccessoryProduct
} from 'utils/manual-accessories';

export default function AccessoriesHydrationWatcher() {
  const dispatch = useAppDispatch();
  const { selectedSystems, selectedDuctedSystems } = useAppSelector(
    (state) => state.systems
  );
  const { ductingComponents } = useAppSelector((state) => state.data);

  const [initialSystemIds, setInitialSystemIds] = useState<Set<string> | null>(
    null
  );

  useEffect(() => {
    if (initialSystemIds !== null) return;

    const ductlessIds = selectedSystems.map((s) => s.uniqueId);
    const ductedIds = selectedDuctedSystems
      .map((s) => s.id?.toString())
      .filter(Boolean);
    const allIds = [...ductlessIds, ...ductedIds];

    if (allIds.length > 0) {
      setInitialSystemIds(new Set(allIds));
    }
  }, [selectedSystems, selectedDuctedSystems, initialSystemIds]);

  const systemsNeedingHydration = useMemo(() => {
    if (!initialSystemIds) return { ductless: [], ducted: [] };

    const ductless = selectedSystems.filter(
      (s) =>
        initialSystemIds.has(s.uniqueId) &&
        !s.tags.includes(COMPATIBILITY_MATCH_TAG) &&
        s.quotableAccessories.length === 0 &&
        s.selectedAccessories.length === 0 &&
        s.configuration.condensers.every(
          (c) => c.selectedAccessories.length === 0
        )
    );
    const ducted = selectedDuctedSystems.filter(
      (s) =>
        initialSystemIds.has(s.id?.toString() ?? '') &&
        s.quotableAccessories.length === 0 &&
        s.selectedAccessories.length === 0 &&
        s.config.condensers.every((c) => c.selectedAccessories.length === 0)
    );
    return { ductless, ducted };
  }, [selectedSystems, selectedDuctedSystems, initialSystemIds]);

  const uniqueIds = useMemo(() => {
    return [
      ...systemsNeedingHydration.ductless.map((s) => s.uniqueId),
      ...systemsNeedingHydration.ducted
        .map((s) => s.id?.toString())
        .filter(Boolean)
    ];
  }, [systemsNeedingHydration]);

  const { data: quotableSystemsData } = useGetQuotableSystemsQuery(uniqueIds, {
    skip: uniqueIds.length === 0
  });

  const { data: recommendationsData } =
    useGetQuotableSystemsAccessoriesRecommendationsQuery(
      { uniqueIds },
      { skip: uniqueIds.length === 0 }
    );

  useEffect(() => {
    if (!quotableSystemsData) return;
    if (!recommendationsData) return;

    quotableSystemsData.forEach((qs) => {
      if (qs.accessories.length === 0) return;

      const systemRecommendations = recommendationsData
        ? recommendationsData.filter((r) => r.systemUniqueId === qs.uniqueId)
        : [];

      const quotableAccessories = qs.accessories.map((acc) => {
        const details = systemRecommendations.find(
          (r) => r.accessoryProductId === acc.productId
        );
        if (details) {
          return {
            productId: acc.productId,
            name: details.accessoryName,
            shortName: details.accessoryShortName,
            description: details.accessoryDescription,
            price: details.accessoryPrice,
            type: details.accessoryOriginalType as AccessoryTypeType,
            productUrl: details.accessoryProductUrl,
            photo: details.accessoryPhoto,
            availableStock: details.accessoryAvailableStock,
            quantity: acc.quantity
          };
        }
        const component = ductingComponents.find(
          (candidate) => candidate.productId === acc.productId
        );
        if (component) {
          return {
            productId: acc.productId,
            name: component.name,
            shortName: component.name,
            description: component.description,
            price: component.price,
            type: 'ducting' as AccessoryTypeType,
            productUrl: component.productUrl,
            photo: component.photo,
            availableStock: component.availableStock,
            quantity: acc.quantity
          };
        }
        if (acc.product) {
          return {
            ...toManualAccessoryProduct(acc.product),
            shortName: acc.product.name,
            quantity: acc.quantity
          };
        }
        // Fallback: create a minimal entry so the accessory is not lost
        return {
          productId: acc.productId,
          name: '',
          shortName: '',
          description: '',
          price: 0,
          type: MANUAL_ACCESSORY_TYPE,
          productUrl: '',
          photo: '',
          availableStock: 0,
          quantity: acc.quantity
        };
      });

      if (quotableAccessories.length === 0) return;

      const ductlessMatch = systemsNeedingHydration.ductless.find(
        (s) => s.uniqueId === qs.uniqueId
      );
      if (ductlessMatch) {
        const condenserAccessories = new Map<
          string,
          Array<(typeof quotableAccessories)[number]>
        >();

        const zoneToCondenser = new Map<string, string>();
        ductlessMatch.configuration.condensers.forEach((c) => {
          c.zones.forEach((z) => {
            zoneToCondenser.set(z.uniqueId, c.uniqueId);
          });
        });

        const unmappedAccessories = qs.accessories.filter((acc) => {
          const qaDetails = quotableAccessories.find(
            (qa) => qa.productId === acc.productId
          );
          if (!qaDetails) return true;

          if (acc.zoneUniqueId) {
            const owningCondenserId = zoneToCondenser.get(acc.zoneUniqueId);
            if (owningCondenserId) {
              const list = condenserAccessories.get(owningCondenserId) ?? [];
              list.push({ ...qaDetails, quantity: acc.quantity });
              condenserAccessories.set(owningCondenserId, list);
              return false;
            }
          }

          if (acc.condenserUniqueId) {
            const list = condenserAccessories.get(acc.condenserUniqueId) ?? [];
            list.push({ ...qaDetails, quantity: acc.quantity });
            condenserAccessories.set(acc.condenserUniqueId, list);
            return false;
          }

          return true;
        });

        if (unmappedAccessories.length > 0) {
          unmappedAccessories.forEach((acc) => {
            const qaDetails = quotableAccessories.find(
              (qa) => qa.productId === acc.productId
            );
            if (!qaDetails) return;

            const matchingRecs = systemRecommendations.filter(
              (r) => r.accessoryProductId === acc.productId
            );

            if (matchingRecs.length === 0) {
              const firstCId =
                ductlessMatch.configuration.condensers[0]?.uniqueId;
              if (firstCId) {
                const list = condenserAccessories.get(firstCId) ?? [];
                list.push({ ...qaDetails, quantity: acc.quantity });
                condenserAccessories.set(firstCId, list);
              }
              return;
            }

            const recsPerCondenser = new Map<string, number>();
            matchingRecs.forEach((rec) => {
              let cId: string | undefined;
              if (rec.productType === 'condenser') {
                cId = rec.uniqueId;
              } else if (rec.productType === 'air_handler') {
                cId = zoneToCondenser.get(rec.uniqueId);
              } else {
                cId = ductlessMatch.configuration.condensers[0]?.uniqueId;
              }
              if (cId) {
                recsPerCondenser.set(cId, (recsPerCondenser.get(cId) ?? 0) + 1);
              }
            });

            let remaining = acc.quantity;
            recsPerCondenser.forEach((recCount, cId) => {
              if (remaining <= 0) return;
              const qty = Math.min(recCount, remaining);
              remaining -= qty;
              const list = condenserAccessories.get(cId) ?? [];
              list.push({ ...qaDetails, quantity: qty });
              condenserAccessories.set(cId, list);
            });

            if (remaining > 0) {
              const firstCId =
                ductlessMatch.configuration.condensers[0]?.uniqueId;
              if (firstCId) {
                const list = condenserAccessories.get(firstCId) ?? [];
                const existing = list.find(
                  (a) => a.productId === acc.productId
                );
                if (existing) {
                  existing.quantity += remaining;
                } else {
                  list.push({ ...qaDetails, quantity: remaining });
                }
                condenserAccessories.set(firstCId, list);
              }
            }
          });
        }

        const updatedCondensers = ductlessMatch.configuration.condensers.map(
          (c) => {
            const rawList =
              condenserAccessories.get(c.uniqueId) ?? c.selectedAccessories;
            const mergedMap = new Map<number, (typeof rawList)[0]>();
            rawList.forEach((acc) => {
              const existing = mergedMap.get(acc.productId);
              if (existing) {
                existing.quantity += acc.quantity;
              } else {
                mergedMap.set(acc.productId, { ...acc });
              }
            });
            return {
              ...c,
              selectedAccessories: Array.from(mergedMap.values())
            };
          }
        );

        dispatch(
          updateSelectedSystem({
            uniqueId: qs.uniqueId,
            quotableAccessories,
            configuration: {
              ...ductlessMatch.configuration,
              condensers: updatedCondensers
            }
          })
        );
      }

      const ductedMatch = systemsNeedingHydration.ducted.find(
        (s) => s.id?.toString() === qs.uniqueId
      );
      if (ductedMatch) {
        const condenserAccessoriesDucted = new Map<
          string,
          Array<(typeof quotableAccessories)[number]>
        >();

        const ductedZoneToCondenser = new Map<string, string>();
        ductedMatch.config.condensers.forEach((c) => {
          c.zones.forEach((z) => {
            ductedZoneToCondenser.set(z.uniqueId, c.uniqueId);
          });
        });

        const unmappedDuctedAccessories = qs.accessories.filter((acc) => {
          const qaDetails = quotableAccessories.find(
            (qa) => qa.productId === acc.productId
          );
          if (!qaDetails) return true;

          if (acc.zoneUniqueId) {
            const owningCondenserId = ductedZoneToCondenser.get(
              acc.zoneUniqueId
            );
            if (owningCondenserId) {
              const list =
                condenserAccessoriesDucted.get(owningCondenserId) ?? [];
              list.push({ ...qaDetails, quantity: acc.quantity });
              condenserAccessoriesDucted.set(owningCondenserId, list);
              return false;
            }
          }

          if (acc.condenserUniqueId) {
            const list =
              condenserAccessoriesDucted.get(acc.condenserUniqueId) ?? [];
            list.push({ ...qaDetails, quantity: acc.quantity });
            condenserAccessoriesDucted.set(acc.condenserUniqueId, list);
            return false;
          }

          return true;
        });

        if (unmappedDuctedAccessories.length > 0) {
          unmappedDuctedAccessories.forEach((acc) => {
            const qaDetails = quotableAccessories.find(
              (qa) => qa.productId === acc.productId
            );
            if (!qaDetails) return;

            const matchingRecs = systemRecommendations.filter(
              (r) => r.accessoryProductId === acc.productId
            );

            if (matchingRecs.length === 0) {
              const firstCId = ductedMatch.config.condensers[0]?.uniqueId;
              if (firstCId) {
                const list = condenserAccessoriesDucted.get(firstCId) ?? [];
                list.push({ ...qaDetails, quantity: acc.quantity });
                condenserAccessoriesDucted.set(firstCId, list);
              }
              return;
            }

            const recsPerCondenser = new Map<string, number>();
            matchingRecs.forEach((rec) => {
              let cId: string | undefined;
              if (rec.productType === 'condenser') {
                cId = rec.uniqueId;
              } else if (rec.productType === 'air_handler') {
                cId = ductedZoneToCondenser.get(rec.uniqueId);
              } else {
                cId = ductedMatch.config.condensers[0]?.uniqueId;
              }
              if (cId) {
                recsPerCondenser.set(cId, (recsPerCondenser.get(cId) ?? 0) + 1);
              }
            });

            let remaining = acc.quantity;
            recsPerCondenser.forEach((recCount, cId) => {
              if (remaining <= 0) return;
              const qty = Math.min(recCount, remaining);
              remaining -= qty;
              const list = condenserAccessoriesDucted.get(cId) ?? [];
              list.push({ ...qaDetails, quantity: qty });
              condenserAccessoriesDucted.set(cId, list);
            });

            if (remaining > 0) {
              const firstCId = ductedMatch.config.condensers[0]?.uniqueId;
              if (firstCId) {
                const list = condenserAccessoriesDucted.get(firstCId) ?? [];
                const existing = list.find(
                  (a) => a.productId === acc.productId
                );
                if (existing) {
                  existing.quantity += remaining;
                } else {
                  list.push({ ...qaDetails, quantity: remaining });
                }
                condenserAccessoriesDucted.set(firstCId, list);
              }
            }
          });
        }

        const updatedCondensers = ductedMatch.config.condensers.map((c) => {
          const rawList =
            condenserAccessoriesDucted.get(c.uniqueId) ?? c.selectedAccessories;
          const mergedMap = new Map<number, (typeof rawList)[0]>();
          rawList.forEach((acc) => {
            const existing = mergedMap.get(acc.productId);
            if (existing) {
              existing.quantity += acc.quantity;
            } else {
              mergedMap.set(acc.productId, { ...acc });
            }
          });
          return {
            ...c,
            selectedAccessories: Array.from(mergedMap.values())
          };
        });

        dispatch(
          updateSelectedDuctedSystem({
            id: ductedMatch.id,
            quotableAccessories,
            config: {
              ...ductedMatch.config,
              condensers: updatedCondensers
            }
          })
        );
      }
    });
  }, [
    quotableSystemsData,
    recommendationsData,
    systemsNeedingHydration,
    ductingComponents,
    dispatch
  ]);

  return null;
}
